// RolContabilidad.js
import { useState, useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "bootstrap-icons/font/bootstrap-icons.css";

const RolContabilidad = ({ isOpen, toggleSidebar, closeSidebar }) => {
  const [openMenus, setOpenMenus] = useState({});
  const [fadeMenus, setFadeMenus] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Función para alternar un submenú
  const toggleMenu = useCallback(
    (menu) => {
      if (!isOpen) {
        toggleSidebar();
        setOpenMenus((prev) => ({ ...prev, [menu]: true }));
        setTimeout(() => {
          setFadeMenus((prev) => ({ ...prev, [menu]: true }));
        }, 10);
      } else {
        const newState = !openMenus[menu];
        setOpenMenus((prev) => ({ ...prev, [menu]: newState }));
        if (newState) {
          setTimeout(() => {
            setFadeMenus((prev) => ({ ...prev, [menu]: true }));
          }, 10);
        } else {
          setFadeMenus((prev) => ({ ...prev, [menu]: false }));
        }
      }
    },
    [isOpen, toggleSidebar, openMenus],
  );

  useEffect(() => {
    // construye un objeto con las claves de tus menús
    const newOpen = {
      Reportes:
        location.pathname.startsWith("/generador") ||
        location.pathname.startsWith("/opcion-2"),
      Formatos:
        location.pathname.startsWith("/carta-envio") ||
        location.pathname.startsWith("/carta-ingresos") ||
        location.pathname.startsWith("/carta-nueva") ||
        location.pathname.startsWith("/carta-poder"),
    };
    setOpenMenus(newOpen);
  }, [location.pathname]);

  // Cierra todos los submenús
  const resetMenus = useCallback(() => {
    setOpenMenus({}); // Resetea todos los menús abiertos
    closeSidebar(); // Cierra el sidebar
  }, [closeSidebar]);

  const isMenuOpen = (menu) => openMenus[menu] || false;

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem("userRole"); // Eliminar el rol de localStorage al cerrar sesión
    navigate("/login");
  };

  // Función personalizada para manejar el clic en los enlaces
  const handleLinkClick = (e, path) => {
    if (location.pathname !== path) {
      navigate(path); // agrega esto si quieres navegación explícita
      closeSidebar(); // solo ciérralo si realmente navegas
    }
    // No hagas nada si estás ya en esa ruta
  };

  // Función personalizada para manejar el clic en los iconos de plus
  /*const handlePlusIconClick = (e, path) => {
    e.preventDefault(); // Evita la navegación predeterminada del enlace
    e.stopPropagation(); // Previene que el evento burbujee al <Link>
    navigate(path, { state: { openModal: true } });
    closeSidebar(); // Cierra el Sidebar
  };*/

  return (
    <div className={`sidebar-container ${isOpen ? "expanded" : "collapsed"}`}>
      <div className="sidebar-header">
        <div className="logo-container-sidebar">
          <Link to="/" onClick={resetMenus}>
            {isOpen ? (
              // Logo grande cuando está expandido
              <img
                className="sidebar-logo logo-expanded"
                src="/img/logo-comercial.svg"
                alt="ICIAMEX logo"
              />
            ) : (
              // Ícono pequeño (ic.svg) cuando está colapsado
              <img
                className="sidebar-logo logo-small"
                src="/img/ic.svg"
                alt="Menu Icon"
              />
            )}
          </Link>
        </div>

        {/* Aquí va el “cuadro” que contiene el ícono de abrir/cerrar */}
        {isOpen ? (
          <div className="toggle-box" onClick={resetMenus}>
            <i className="bi bi-chevron-left"></i>
          </div>
        ) : (
          <div className="toggle-box" onClick={toggleSidebar}>
            <i className="bi bi-chevron-right"></i>
          </div>
        )}
      </div>

      <li className="separator separator-logo" aria-hidden="true"></li>

      <div className="sidebar-content">
        <nav id="primary-menu" className="navbar">
          <ul className="nav navbar-nav">
            {isOpen && <li className="menu-section">Principal</li>}

            <li
              className={`menu-item ${
                location.pathname === "/" ? "active" : ""
              }`}
              data-tooltip="Inicio"
              onClick={() => handleLinkClick("/", "/")}
            >
              <Link to="/">
                <i className="bi bi-house"></i>
                {isOpen && <span className="item-text">Inicio</span>}
              </Link>
            </li>

            <li
              className={`menu-item ${
                location.pathname === "/tareas" ? "active" : ""
              }`}
              data-tooltip="Tareas"
              onClick={(e) => handleLinkClick(e, "/tareas")}
            >
              <Link to="/tareas">
                <i className="bi bi-check2-square"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Tareas</span>
                  </>
                )}
              </Link>
            </li>

            <li
              className={`menu-item ${
                location.pathname === "/calendario" ? "active" : ""
              }`}
              data-tooltip="Calendario"
              onClick={(e) => handleLinkClick(e, "/calendario")}
            >
              <Link to="/calendario">
                <i className="bi bi-calendar2-day"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Calendario</span>
                  </>
                )}
              </Link>
            </li>

            <li className="separator" aria-hidden="true"></li>

            {isOpen && <li className="menu-section">Utilidades</li>}

            <li
              className={[
                "menu-item",
                "submenu-parent",
                isMenuOpen("Reportes") ? "open" : "",
                location.pathname === "/generador" ||
                location.pathname === "/opcion-2"
                  ? "active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tooltip="Reportes"
              onClick={() => toggleMenu("Reportes")}
            >
              <div>
                <i className="bi bi-file-earmark-bar-graph"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Reportes</span>
                    <i
                      className={`bi ${
                        isMenuOpen("Reportes")
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } arrow`}
                    />
                  </>
                )}
              </div>
            </li>

            {isMenuOpen("Reportes") && (
              <ul
                className={`submenu ${
                  fadeMenus.Reportes ? "submenu-fade" : ""
                } ${isMenuOpen("Reportes") ? "open" : "closed"}`}
              >
                {/* —————————————————— Generador de Obra —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/generador"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/generador")}
                  >
                    <span className="submenu-label">Generador de Obra</span>
                  </Link>
                </li>

                {/* —————————————————— Opción 2 —————————————————— */}
                <li className="submenu-item">
                  <Link to="#" className="submenu-link" onClick={() => {}}>
                    <span className="submenu-label">Opción 2</span>
                  </Link>
                </li>
              </ul>
            )}

            <li
              className={[
                "menu-item",
                "submenu-parent",
                isMenuOpen("Formatos") ? "open" : "",
                location.pathname === "/carta-ingresos" ||
                location.pathname === "/carta-envio" ||
                location.pathname === "/carta-nueva" ||
                location.pathname === "/carta-poder"
                  ? "active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tooltip="Formatos"
              onClick={() => toggleMenu("Formatos")}
            >
              <div>
                <i className="bi bi-file-earmark-post"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Formatos</span>
                    <i
                      className={`bi ${
                        isMenuOpen("Formatos")
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } arrow`}
                    />
                  </>
                )}
              </div>
            </li>

            {isMenuOpen("Formatos") && (
              <ul
                className={`submenu ${
                  fadeMenus.Formatos ? "submenu-fade" : ""
                } ${isMenuOpen("Formatos") ? "open" : "closed"}`}
              >
                {/* —————————————————— Todos —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/carta-ingresos"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/carta-ingresos")}
                  >
                    <span className="submenu-label">Carta Ingresos</span>
                  </Link>
                </li>

                {/* —————————————————— Clientes —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/carta-envio"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/carta-envio")}
                  >
                    <span className="submenu-label">Carta Envió</span>
                  </Link>
                </li>

                {/* —————————————————— Proveedores —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/carta-nueva"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/carta-nueva")}
                  >
                    <span className="submenu-label">Carta Nueva</span>
                  </Link>
                </li>

                <li className="submenu-item">
                  <Link
                    to="/carta-poder"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/carta-poder")}
                  >
                    <span className="submenu-label">Carta Poder</span>
                  </Link>
                </li>
              </ul>
            )}

            <li className="separator" aria-hidden="true"></li>
          </ul>
        </nav>

        <div className="profile-header">
          <img
            className="profile-avatar"
            src="/img/plan.svg"
            alt="Avatar Usuario"
          />
          <div className="profile-text">
            <span style={{color: "#fff"}}>Timeweb</span>
            <span className="profile-role">Contabilidad</span>
          </div>
          <i className="bi bi-chevron-down"></i>
        </div>

        {/* ─── Botón de Cerrar Sesión (footer) ───────────────────────────────────────────────── */}
        {isOpen ? (
          <div className="sidebar-footer">
            <div className="profile-expanded">
              <button className="btn-logout-extended" onClick={handleLogout}>
                <i className="bi bi-box-arrow-in-right"></i>
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        ) : (
          <li
            className="menu-item logout"
            data-tooltip="Cerrar Sesión"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-in-right"></i>
          </li>
        )}

        <Outlet />
      </div>
    </div>
  );
};

export default RolContabilidad;
